import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import config from '../../config/env';
import CategoryLucideIcon from '../categories/CategoryLucideIcon';
import { splitIntoColumns } from '../../utils/categoryGroups';

const CategoriesMenu = ({ isActive }) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuTop, setMenuTop] = useState(64);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    if (!open || categories.length) return;
    fetch(`${config.apiUrl || 'http://localhost:5000'}/api/categories`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setCategories(data.data.categories || []);
      })
      .catch(() => {});
  }, [open, categories.length]);

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const sticky = triggerRef.current?.closest('[data-site-header]');
      if (sticky) {
        setMenuTop(sticky.getBoundingClientRect().bottom);
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const columns = splitIntoColumns(categories, 4);

  const megaPanel = open && categories.length > 0 && createPortal(
    <div
      ref={panelRef}
      className="fixed left-0 right-0 z-[240] bg-white border-b border-gray-200 shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
      style={{ top: menuTop }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className="container mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-1">
          {columns.map((col, colIdx) => (
            <ul key={colIdx} className="space-y-0.5">
              {col.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/events?category=${cat.id}`}
                    className="flex items-center gap-3 py-2.5 group rounded-lg hover:bg-gray-50/80 px-1 -mx-1"
                    onClick={() => setOpen(false)}
                  >
                    <CategoryLucideIcon
                      name={cat.name}
                      size={22}
                      strokeWidth={1.5}
                      className="shrink-0 text-gray-600 transition-colors group-hover:text-orange-600"
                    />
                    <span className="text-[15px] font-semibold text-gray-900 group-hover:text-orange-600 transition-colors leading-snug">
                      {cat.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Explore events by category across Kenya and Africa.
          </p>
          <Link
            to="/categories"
            className="text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
            onClick={() => setOpen(false)}
          >
            View all categories →
          </Link>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={triggerRef}
        className="relative hidden md:block"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <Link
          to="/categories"
          className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            isActive('/categories') || open
              ? 'text-orange-600 bg-orange-50'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Categories
          <i className={`fas fa-chevron-down text-[9px] text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Link>
      </div>
      {megaPanel}
    </>
  );
};

export default CategoriesMenu;
